import {
  attachFloatingFrameDisplays,
  crossesFloatingFrameDisplay,
  floatingFrameDisplayForPoint,
  getFloatingFrameDisplayArea,
  rememberFloatingFrameDisplay,
} from "./floating-frame-displays";
import { listen } from "@tauri-apps/api/event";
import {
  calculateFocusWindowBox,
  focusWindowModeForPoint,
  type FocusWindowBox,
} from "@/lib/focus-window-layout";
import { isTauri } from "@/lib/is-tauri";
import {
  clampFrameHeaderToSurface,
  restoredFrameBoxForPointer,
} from "@/lib/frame-window-gestures";
import { getWindowDisplayId } from "./use-window-role";
import { getWindowOrigin, primeWindowOrigin } from "./use-window-origin";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";
import {
  putFloatingFrame,
  nextFloatingFrameOrder,
  useFloatingFrameStore,
  type FloatingFrame,
} from "./floating-frame-store";
import { captureDesktopChatAuthScopeGuard } from "./desktop-chat-auth-scope";

export interface FrameDragCursor {
  displayId: number;
  x: number;
  y: number;
  primaryDown?: boolean;
  displayIds?: number[];
  workArea?: FocusWindowBox;
}
interface Drag {
  frame: FloatingFrame;
  cursorDisplayId: number;
  offsetX: number;
  offsetY: number;
  transferred: boolean;
  isCurrent: () => boolean;
  onTransfer?: () => void;
}
let active: Drag | null = null;

export function floatingFrameDragActive(frameId: string): boolean {
  return active?.frame.frameId === frameId && active.transferred;
}

/** Source webview owns the complete gesture; crossing never transfers capture. */
export function beginFloatingFrameDrag(
  frame: FloatingFrame,
  event: { clientX: number; clientY: number },
  options: { waitForCrossing?: boolean; onTransfer?: () => void } = {},
) {
  if (!isTauri()) return;
  primeWindowOrigin();
  const origin = getWindowOrigin();
  const pointer = { x: event.clientX + origin.x, y: event.clientY + origin.y };
  const box =
    frame.mode === "normal"
      ? frame.box
      : restoredFrameBoxForPointer(pointer, frame.box, frame.normalBox);
  active = {
    cursorDisplayId: frame.displayId,
    frame: {
      ...frame,
      box,
      normalBox: box,
      mode: "normal",
      dragging: true,
      order: nextFloatingFrameOrder(),
    },
    offsetX: pointer.x - box.x,
    offsetY: pointer.y - box.y,
    transferred: !options.waitForCrossing,
    isCurrent: captureDesktopChatAuthScopeGuard(),
    onTransfer: options.onTransfer,
  };
  if (active.transferred) putFloatingFrame(active.frame);
}

export function moveFloatingFrameDrag(cursor: FrameDragCursor) {
  // Exactly the cursor display recovers windows whose monitor was unplugged.
  // Never use an empty topology sample during display sleep as a removal.
  if (
    cursor.displayId === getWindowDisplayId() &&
    cursor.displayIds?.length &&
    cursor.workArea
  ) {
    for (const frame of Object.values(
      useFloatingFrameStore.getState().frames,
    )) {
      if (cursor.displayIds.includes(frame.displayId)) continue;
      const surface = cursor.workArea;
      const width = Math.min(
        frame.normalBox.width,
        Math.max(1, surface.width - 48),
      );
      const box = {
        x: surface.x + Math.max(0, surface.width - width - 24),
        y: surface.y + 24,
        width,
        height: Math.min(
          frame.normalBox.height,
          Math.max(1, surface.height - 48),
        ),
      };
      putFloatingFrame({
        ...frame,
        displayId: cursor.displayId,
        box,
        normalBox: box,
        dragging: false,
        mode: "normal",
        snapPreview: undefined,
      });
    }
  }
  const drag = active;
  if (!drag) return;
  if (
    !drag.isCurrent() ||
    (drag.transferred &&
      !useFloatingFrameStore.getState().frames[drag.frame.frameId])
  ) {
    active = null;
    return;
  }
  const proposedBox = {
    ...drag.frame.box,
    x: cursor.x - drag.offsetX,
    y: cursor.y - drag.offsetY,
  };
  if (cursor.workArea)
    rememberFloatingFrameDisplay(cursor.displayId, cursor.workArea);
  const crossesEdge = crossesFloatingFrameDisplay(
    proposedBox,
    drag.frame.sourceDisplayId,
  );
  if (
    !drag.transferred &&
    (cursor.displayId !== drag.frame.sourceDisplayId || crossesEdge)
  ) {
    drag.transferred = true;
    drag.onTransfer?.();
  }
  if (!drag.transferred) {
    if (cursor.primaryDown === false) active = null;
    return;
  }
  drag.cursorDisplayId = cursor.displayId;
  const area = cursor.workArea ?? getFloatingFrameDisplayArea(cursor.displayId);
  const box = area ? clampFrameHeaderToSurface(proposedBox, area) : proposedBox;
  const mode = area ? focusWindowModeForPoint(cursor, area) : null;
  const snapPreview =
    mode && area
      ? { mode, box: calculateFocusWindowBox(mode, area) }
      : undefined;
  const done = cursor.primaryDown === false;
  // A cursor crossing alone must not jump the glass to a tiny sliver of the
  // frame. Keep its owner until the destination contains most of the frame.
  const overlap = area
    ? Math.max(
        0,
        Math.min(box.x + box.width, area.x + area.width) -
          Math.max(box.x, area.x),
      ) *
      Math.max(
        0,
        Math.min(box.y + box.height, area.y + area.height) -
          Math.max(box.y, area.y),
      )
    : 0;
  drag.frame = {
    ...drag.frame,
    box: done && snapPreview ? snapPreview.box : box,
    normalBox: box,
    displayId:
      done || overlap > (box.width * box.height) / 2
        ? cursor.displayId
        : drag.frame.displayId,
    dragging: !done,
    mode: done && mode ? mode : "normal",
    snapPreview: done ? undefined : snapPreview,
  };
  const current = useFloatingFrameStore.getState().frames[drag.frame.frameId];
  // A patch to the window's content during the drag (a chat rebinding)
  // stays: the drag owns the geometry only.
  if (current) drag.frame = { ...drag.frame, frame: current.frame };
  if (
    !current ||
    current.box.x !== drag.frame.box.x ||
    current.box.y !== drag.frame.box.y ||
    current.displayId !== drag.frame.displayId ||
    current.dragging !== drag.frame.dragging ||
    current.snapPreview?.mode !== drag.frame.snapPreview?.mode
  ) {
    putFloatingFrame(drag.frame);
  }
  if (done) active = null;
}

export function endFloatingFrameDrag(event?: {
  clientX: number;
  clientY: number;
}) {
  const drag = active;
  if (!drag) return;
  if (event && drag.transferred) {
    const origin = getWindowOrigin();
    const point = { x: event.clientX + origin.x, y: event.clientY + origin.y };
    const display = floatingFrameDisplayForPoint(point, drag.cursorDisplayId);
    if (display) {
      // Pointerup may enter or leave an edge after the last native poll.
      moveFloatingFrameDrag({ ...point, ...display, primaryDown: false });
      return;
    }
  }
  const current = useFloatingFrameStore.getState().frames[drag.frame.frameId];
  if (drag.transferred && drag.isCurrent() && current) {
    const preview = drag.frame.snapPreview;
    const area = getFloatingFrameDisplayArea(drag.cursorDisplayId);
    const normalBox = area
      ? clampFrameHeaderToSurface(drag.frame.normalBox, area)
      : drag.frame.normalBox;
    putFloatingFrame({
      ...drag.frame,
      frame: current.frame,
      displayId: drag.cursorDisplayId,
      dragging: false,
      box: preview?.box ?? normalBox,
      normalBox,
      mode: preview?.mode ?? "normal",
      snapPreview: undefined,
    });
  }
  active = null;
}

export function toggleFloatingFrameWindow(frameId: string) {
  const frame = useFloatingFrameStore.getState().frames[frameId];
  if (!frame || frame.displayId !== getWindowDisplayId()) return;
  const origin = getWindowOrigin();
  const mode = frame.mode === "normal" ? "maximized" : "normal";
  putFloatingFrame({
    ...frame,
    mode,
    box: calculateFocusWindowBox(
      mode,
      {
        ...origin,
        width: window.innerWidth,
        height: window.innerHeight,
      },
      frame.normalBox,
    ),
  });
}

export function attachFloatingFrameDrag(): () => void {
  if (!isTauri()) return () => {};
  primeWindowOrigin();
  const detachDisplays = attachFloatingFrameDisplays();
  let disposed = false;
  const unlisteners: Array<() => void> = [];
  for (const name of ["cursor-position", "active-display-changed"]) {
    void listen<FrameDragCursor>(name, ({ payload }) =>
      moveFloatingFrameDrag(payload),
    ).then((unlisten) => {
      if (disposed) safeTauriUnlisten(unlisten);
      else unlisteners.push(unlisten);
    });
  }
  // Native primaryDown is authoritative if the source DOM was unmounted.
  window.addEventListener("pointerup", endFloatingFrameDrag);
  return () => {
    disposed = true;
    detachDisplays();
    active = null;
    window.removeEventListener("pointerup", endFloatingFrameDrag);
    unlisteners.forEach(safeTauriUnlisten);
  };
}
